import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const ATT_OPTIONS = [
  { v: '1', label: '1 Asistió', cls: 'bg-green-100 text-green-700 border-green-300' },
  { v: '0', label: '0 Falta', cls: 'bg-red-100 text-red-700 border-red-300' },
  { v: 'R', label: 'R Retardo', cls: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { v: 'J', label: 'J Justificada', cls: 'bg-green-100 text-green-700 border-green-300' },
];

// Shared value + note popover for Asistencias ('attendance') and Evaluación ('grade' 0-100).
export const CellNoteDialog = ({ open, mode, prefix, studentId, colId, initialValue, initialText, isReadOnly, onClose, onSave }) => {
  const [value, setValue] = useState(initialValue);
  const [text, setText] = useState(initialText);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setValue(initialValue); setText(initialText); }
  }, [open, studentId, colId, initialValue, initialText]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(studentId, colId, String(value || ''), String(text || '').slice(0, 280));
    } catch (e) {
      console.error('Error saving note:', e);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md bg-white" data-testid={`${prefix}-note-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-600" />
            {mode === 'attendance' ? 'Asistencia y justificación' : 'Calificación y justificación'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm">{mode === 'attendance' ? 'Registro' : 'Calificación (%)'}</Label>
            {mode === 'attendance' ? (
              <div className="flex flex-wrap gap-2 mt-1">
                {ATT_OPTIONS.map(o => (
                  <button key={o.v} type="button" disabled={isReadOnly} onClick={() => setValue(o.v)}
                    className={`px-3 py-1.5 text-sm font-bold rounded-lg border-2 ${o.cls} ${value === o.v ? 'ring-2 ring-indigo-500' : 'opacity-60'}`}
                    data-testid={`${prefix}-note-value-${o.v}`}>{o.label}</button>
                ))}
              </div>
            ) : (
              <input type="text" inputMode="numeric" maxLength={3} value={value}
                onChange={(e) => {
                  const raw = e.target.value.replace('%', '');
                  if (raw === '' || (/^\d{1,3}$/.test(raw) && parseInt(raw, 10) <= 100)) setValue(raw);
                }}
                disabled={isReadOnly} className="mt-1 w-24 h-9 px-3 text-center font-bold border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400"
                data-testid={`${prefix}-note-value-input`} />
            )}
          </div>
          <div>
            <Label className="text-sm">Justificación / nota (máx. 280)</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 280))}
              disabled={isReadOnly} rows={4} maxLength={280}
              placeholder={isReadOnly ? 'Sin justificación' : 'Explica el contenido de la casilla'}
              className="mt-1 resize-none" data-testid={`${prefix}-note-text-input`} />
            <p className="text-xs text-slate-400 mt-1 text-right">{(text || '').length}/280</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} data-testid={`${prefix}-note-cancel-btn`}>
            {isReadOnly ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid={`${prefix}-note-save-btn`}>
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const NoteDot = ({ testId }) => (
  <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-indigo-500 border border-white"
    data-note-dot="true" data-testid={testId} title="Tiene justificación" />
);
